import { createContext, type ReactNode, useCallback, useContext, useRef, useSyncExternalStore } from 'react';
import { markMissingTargetingErrors } from '../utils/markMissingTargetingErrors';
import { transformToTargetApiObject } from '../utils/transformToTargetApiObject';
import type { OnTargetingUpdateFn, RequiredParamsType, SetRequiredParams, TargetingSettingsContextType } from './types';

const TargetingSettingsContext = createContext<null | TargetingSettingsContextType>(null);

const createTargetingSettingsStore = (initialState: RequiredParamsType, onTargetingUpdate?: OnTargetingUpdateFn) => {
  let state: RequiredParamsType | null = initialState;

  const listeners = new Set<() => void>();

  const getState = () => state;

  const setState: TargetingSettingsContextType['setState'] = (updater, options) => {
    const nextState = typeof updater === 'function' ? updater(state as RequiredParamsType) : updater;
    if (nextState === state) return;

    markMissingTargetingErrors(nextState);

    /** Initial hydration only mirrors the value the host form already holds - see `SetRequiredParamsOptions`. */
    if (!options?.silent) {
      onTargetingUpdate?.(transformToTargetApiObject(nextState));
    }

    state = nextState;

    listeners.forEach(listener => {
      listener();
    });
  };

  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  return { getState, setState, subscribe };
};

type ProviderProps = {
  children: ReactNode;
  initialState: RequiredParamsType;
  onTargetingUpdate?: OnTargetingUpdateFn;
};

export const TargetingSettingsProvider = ({ children, initialState, onTargetingUpdate }: ProviderProps) => {
  const storeRef = useRef<TargetingSettingsContextType | null>(undefined);

  if (!storeRef.current) {
    storeRef.current = createTargetingSettingsStore(initialState, onTargetingUpdate);
  }

  return <TargetingSettingsContext.Provider value={storeRef.current}>{children}</TargetingSettingsContext.Provider>;
};

export const useTargetingSettingsContext = <T,>(selector: (state: RequiredParamsType | null) => T): T => {
  const store = useContext(TargetingSettingsContext);

  if (!store) throw new Error('useTargetingSettingsContext must be used inside TargetingSettingsProvider');

  const getSnapshot = useCallback(() => selector(store.getState()), [store, selector]);

  return useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot);
};

export const useRequiredParamsSetter = (): SetRequiredParams => {
  const store = useContext(TargetingSettingsContext);

  if (!store) throw new Error('useTargetingSettingsContext must be used inside TargetingSettingsProvider');

  return store.setState;
};
